import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CART_KEY = "ai4faculty_cart_v1";
const ALLOWED_IDS = new Set(["module-1", "module-2", "module-3", "module-4", "module-5", "module-6"]);

const CartCtx = createContext({
    items: [],
    add: () => {},
    remove: () => {},
    clear: () => {},
    has: () => false,
    count: 0,
});

function readStorage() {
    try {
        const raw = localStorage.getItem(CART_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((id) => ALLOWED_IDS.has(id));
    } catch {
        return [];
    }
}

function writeStorage(items) {
    try {
        localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
        /* localStorage disabled — non-fatal */
    }
}

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);

    useEffect(() => {
        setItems(readStorage());
    }, []);

    // Cross-tab sync
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === CART_KEY) setItems(readStorage());
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const persist = useCallback((next) => {
        setItems(next);
        writeStorage(next);
    }, []);

    const add = useCallback((moduleId) => {
        if (!ALLOWED_IDS.has(moduleId)) return false;
        setItems((prev) => {
            if (prev.includes(moduleId)) return prev;
            const next = [...prev, moduleId];
            writeStorage(next);
            return next;
        });
        return true;
    }, []);

    const remove = useCallback((moduleId) => {
        setItems((prev) => {
            const next = prev.filter((id) => id !== moduleId);
            writeStorage(next);
            return next;
        });
    }, []);

    const clear = useCallback(() => persist([]), [persist]);

    const has = useCallback((moduleId) => items.includes(moduleId), [items]);

    const value = useMemo(
        () => ({ items, add, remove, clear, has, count: items.length }),
        [items, add, remove, clear, has],
    );

    return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => useContext(CartCtx);
